"use client";

import { useUser } from "@clerk/nextjs";
import {
  cloneElement,
  ComponentPropsWithRef,
  forwardRef,
  ReactElement,
  useState,
} from "react";

import Link from "next/link";
import { StarIcon } from "@heroicons/react/24/outline";

import { Button, type ButtonProps } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { YouTubeEmbed } from "@/app/_components/youtube-embed";
import { PriceList } from "@/app/_components/price-list";
import { PriceCard } from "@/app/_components/price-card";
import { Logo } from "@/app/_components/logo";

import { getUserPlanByMetadata } from "@/helper/user/client";

import { cn } from "@/lib/utils";
import { plans, billingPeriod, emailSupport, phoneSupport } from "@/constants";
import { pricingSearchParam } from "@/filters";

import stars from "@/static/stars.json";

const Price = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const toggle = (checked: boolean) => {
    setIsAnnual(checked);
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6 bg-white sm:bg-transparent py-4 border-t-2 sm:py-0 sm:border-t-0 z-10">
      <PriceCard.Price
        switchAlign="start"
        plan={plans.especialista.value}
        price={plans.especialista.price}
        isAnnual={isAnnual}
        toggle={toggle}
      />
      <Button size="lg" asChild className="shadow-lg">
        <Link
          href={`/checkout?${pricingSearchParam.plano}=${plans.especialista.value}&${pricingSearchParam.periodo}=${isAnnual ? billingPeriod.anual : billingPeriod.mensal}`}
          target="_blank"
        >
          Testar grátis por 5 dias
        </Link>
      </Button>
      <p className="text-muted-foreground/80 text-xs">
        Você só paga após 5 dias grátis. Dúvidas? Fale conosco por{" "}
        <a
          href={`https://wa.me/${phoneSupport}?text=Olá,%20estou%20entrando%20em%20contato%20atráves%20do%20Pro%20Leilão...`}
          target="_blank"
          rel="nofollow"
          className="underline"
        >
          WhatsApp
        </a>{" "}
        ou pelo email:{" "}
        <a href={`mailto:${emailSupport}`} className="underline">
          {emailSupport}
        </a>
      </p>
    </div>
  );
};

interface RequiredBuyPremiumButtonProps
  extends Pick<ButtonProps, "variant" | "size">,
    ComponentPropsWithRef<"button"> {
  children?: ReactElement | null;
  enabled?: boolean;
  label?: React.ReactNode;
}

export const RequiredBuyPremiumButton = forwardRef<
  HTMLButtonElement,
  RequiredBuyPremiumButtonProps
>(({ children = null, enabled, variant, size, label, ...props }, ref) => {
  let user, hasPaidPlan;

  try {
    const clerkUser = useUser();
    user = clerkUser?.user;
    hasPaidPlan = getUserPlanByMetadata(user?.publicMetadata)?.hasPaidPlan;
  } catch (error) {}

  if (hasPaidPlan || enabled) {
    if (!children) return null;
    return cloneElement(children, {
      ref,
      ...props,
    } as React.JSX.IntrinsicAttributes);
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        {!label && children ? (
          cloneElement(children, {
            ref,
            role: "button",
            className: cn("m-0", (children as any)?.props?.className),
          } as React.JSX.IntrinsicAttributes)
        ) : (
          <Button
            ref={ref}
            variant={variant}
            size={size}
            className="w-full m-auto"
          >
            {label ?? "Testar grátis por 5 dias"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="overflow-auto sm:overflow-hidden w-full sm:max-w-6xl h-full sm:h-[95%]">
        <div className="flex flex-col sm:flex-row gap-8 h-full">
          {/* Scrollable Left Column */}
          <div className="flex-grow overflow-y-auto sm:h-[800px] pr-4 sm:pr-14 sm:pb-36 sm:border-r-2 sm:border-r-gray-200 no-scrollbar">
            <div className="flex flex-col gap-12">
              <div className="flex flex-col gap-4">
                <Logo />
                <DialogTitle className="text-2xl sm:text-3xl font-bold">
                  Seja o primeiro a saber de novos leilões na sua cidade.
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  Pare de perder horas pesquisando e analisando imóveis. Ative
                  os alertas e saia na frente da concorrência, receba
                  notificações, calcule viabilidade, analise de documentos e
                  economize tempo.
                </DialogDescription>
              </div>
              <div className="w-full h-11/12 rounded-md overflow-hidden border-[1px] border-gray-200 shadow-md">
                <YouTubeEmbed
                  videoid="-2nKF--MkPI"
                  playlabel="Apresentação da Plataforma - Pro Leilão"
                  params="rel=0&autoplay=1"
                />
              </div>
              <div className="flex flex-col gap-10">
                <div className="flex flex-col gap-2">
                  <p>
                    ✅{" "}
                    <strong>
                      Ganhe acesso a todos os imóveis da lista de forma
                      ilimitada
                    </strong>{" "}
                    e busque por endereço, cidade, bairro e muito mais
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-filtro-estados-cidades.png"
                      alt="Filtros avançados para imóveis de Leilão"
                      loading="eager"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p>
                    ✅{" "}
                    <strong>
                      Habilite todos os filtros para escolher imóveis de acordo
                      com a sua preferência
                    </strong>{" "}
                    e encontre os melhores imóveis para comprar
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-filtro.png"
                      loading="eager"
                      alt="Filtros avançados para imóveis de Leilão"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p>
                    ✅{" "}
                    <strong>
                      Receba alertas sobre leilões imobiliários que combinam com
                      seu perfil.
                    </strong>{" "}
                    Nunca perca uma oportunidade!
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-alertas.png"
                      loading="eager"
                      alt="Filtros avançados para imóveis de Leilão"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p>
                    ✅ <strong>Calcule os lucros estimados</strong> com base no
                    preço do imóvel, custos iniciais e potencial de valorização.
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-calculadora-lucro.png"
                      loading="eager"
                      alt="Filtros avançados para imóveis de Leilão"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p>
                    ✅{" "}
                    <strong>
                      Descubra informações essenciais sobre o histórico do
                      imóvel
                    </strong>{" "}
                    como transferências de propriedade, ônus, hipotecas e muito
                    mais.
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-analise-matricula-imovel.png"
                      loading="eager"
                      alt="Filtros avançados para imóveis de Leilão"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <p>
                    ✅{" "}
                    <strong>
                      Gere documentos personalizados com informações do imóvel e
                      do ocupante
                    </strong>{" "}
                    otimizando o processo de desocupação com eficiência e
                    precisão.
                  </p>
                  <div className="rounded-md border-2 border-neutral-200 shadow-sm overflow-hidden">
                    <img
                      src="/leilao-intimacao-legal.png"
                      loading="eager"
                      alt="Filtros avançados para imóveis de Leilão"
                    />
                  </div>
                </div>
                <p>
                  ✅ <strong>Ganhe acesso a planilha excel de imóveis</strong>{" "}
                  de todo o Brasil
                </p>
                <p>
                  ✅{" "}
                  <strong>
                    Seja o primeiro a saber de novos leilão de imóveis
                  </strong>
                </p>
              </div>
            </div>
            <div className="hidden sm:block pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-200/90 to-transparent" />
          </div>
          {/* Sticky Right Column */}
          <div className="flex-shrink-0 flex flex-col gap-6 flex-grow overflow-y-auto sm:h-[800px] pr-4 sm:pb-28 sm:max-w-sm">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 items-center justify-center">
                <div className="flex flex-col items-center justify-center gap-1">
                  <div className="flex items-center justify-center gap-1 w-full">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <StarIcon
                        key={index}
                        className="w-7 h-7 text-primary fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Nota dos clientes <strong>9,5</strong> |{" "}
                    <strong>1.960</strong> avaliações
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  {stars.map((item, index) => (
                    <div
                      key={index}
                      className="-mr-2 rounded-full border-secondary border-2"
                      style={{ zIndex: index }}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage
                          src={item.src}
                          loading="eager"
                          alt={`Pessoa investidor leilão de imóveis ${index}`}
                        />
                        <AvatarFallback className="uppercase">
                          {item.fallback}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-md p-2 border border-primary">
                <p className="text-primary text-xs font-medium text-center">
                  4.789 membros + 197 pessoas entraram esse mês
                </p>
              </div>
            </div>
            <PriceList size="small" features={plans.especialista.features} />
            <div className="hidden sm:block sm:pb-60">
              <Price />
            </div>
          </div>
        </div>
        <div className="block z-10 sticky -bottom-6 sm:hidden">
          <Price />
        </div>
      </DialogContent>
    </Dialog>
  );
});

RequiredBuyPremiumButton.displayName = "RequiredBuyPremiumButton";
